import { useLocation, Link } from "wouter";
import { NO_APP_CHROME_ROUTES } from "@/lib/appChrome";
import {
  Home,
  Sparkles,
  Heart,
  MessageCircle,
  LayoutGrid,
  Sun,
  Flame,
  BookOpen,
  TreeDeciduous,
  Users,
  Gamepad2,
  TrendingUp,
  BarChart3,
  User,
  GraduationCap,
  Star,
  Hash,
  Telescope,
  Crown,
  Gem,
  LogOut,
  ShieldCheck,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  CircleDollarSign,
  FileText,
  Megaphone,
  MessageSquare,
  LayoutDashboard,
  CalendarDays,
  Bell,
  type LucideIcon,
} from "lucide-react";
import { getExploreSections } from "@workspace/manual";
import FeaturePlanMarker from "@/components/FeaturePlanMarker";
import { useUsageSummary } from "@/components/UsageMeterCard";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useLanguage } from "@/hooks/useLanguage";
import SettingsToggle from "@/components/SettingsToggle";
import { apiRequest } from "@/lib/queryClient";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import {
  ADMIN_SECTIONS,
  canAccessAdminSection,
  getAccountRoleLabel,
  getAdminControl,
  isActiveAdminSection,
  isAdminRoute,
} from "@/lib/adminAccess";

const NAV_ITEMS: { path: string; labelKey: string; fallback: string; icon: LucideIcon }[] = [
  { path: "/home", labelKey: "nav.home", fallback: "Home", icon: Home },
  { path: "/compatibility", labelKey: "nav.match_short", fallback: "Match", icon: Heart },
  { path: "/tarot", labelKey: "nav.tarot_short", fallback: "Tarot", icon: Sparkles },
  { path: "/relationship-coach", labelKey: "nav.coach_short", fallback: "Coach", icon: MessageCircle },
];

const EXPLORE_ICONS: Record<string, LucideIcon> = {
  user: User,
  grid: LayoutGrid,
  heart: Heart,
  users: Users,
  search: Sparkles,
  sun: Sun,
  calendar: CalendarDays,
  sunrise: Sun,
  clock: CalendarDays,
  bell: Bell,
  shield: ShieldCheck,
  hexagon: Gem,
  hash: Hash,
  layers: Star,
  aperture: User,
  image: User,
  "message-circle": MessageCircle,
  home: TreeDeciduous,
  "edit-3": BookOpen,
  "trending-up": TrendingUp,
  coffee: Flame,
  briefcase: Users,
  "book-open": BookOpen,
  "check-circle": BarChart3,
  award: Gamepad2,
  telescope: Telescope,
  zap: Crown,
  star: Star,
};

const PRIMARY_NAV_PATHS = new Set(NAV_ITEMS.map((item) => item.path));

export const MORE_SECTIONS = getExploreSections("web")
  .map((section) => ({
    headingKey: `nav.section.${section.id}`,
    headingFallback: section.title,
    items: section.items
      .filter((feature) => !PRIMARY_NAV_PATHS.has(feature.path))
      .map((feature) => ({
        path: feature.path,
        labelKey: feature.labelKey,
        fallback: feature.label,
        icon: EXPLORE_ICONS[feature.icon] ?? Sparkles,
        id: feature.id,
        planTiers: feature.planTiers,
      })),
  }))
  .filter((section) => section.items.length > 0);

const ADMIN_SECTION_ICONS = {
  "": LayoutDashboard,
  analytics: BarChart3,
  applications: ClipboardCheck,
  payouts: CircleDollarSign,
  abuse: ShieldCheck,
  bookings: CalendarDays,
  users: Users,
  payments: CircleDollarSign,
  notifications: Megaphone,
  feedback: MessageSquare,
  backlinks: Telescope,
  audit: FileText,
  vastu: TreeDeciduous,
  "vastu-operations": ShieldCheck,
  access: ShieldCheck,
} as const;

export default function BottomNav() {
  const [location, setLocation] = useLocation();
  const { t } = useLanguage();
  const [moreOpen, setMoreOpen] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: usageSummary } = useUsageSummary(!!user);
  const effectiveTier = usageSummary?.tier;
  const adminControl = getAdminControl(user);
  const roleLabel = getAccountRoleLabel(user);
  const adminRouteOpen = isAdminRoute(location);
  const [adminMenuOpen, setAdminMenuOpen] = useState(adminRouteOpen);

  useEffect(() => {
    if (adminRouteOpen) setAdminMenuOpen(true);
  }, [adminRouteOpen]);

  if (NO_APP_CHROME_ROUTES.includes(location)) return null;

  const handleLogout = async () => {
    try {
      await apiRequest("POST", "/api/auth/logout");
    } catch {
      // ignore — still clear and redirect
    }
    queryClient.clear();
    setMoreOpen(false);
    setLocation("/login");
  };

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
        <div className="bg-background/95 backdrop-blur-xl border-t border-border/50 px-2 pb-[env(safe-area-inset-bottom)]">
          <div className="flex min-h-14 items-stretch">
            {NAV_ITEMS.map((item) => {
              const isActive = location === item.path || (item.path === "/home" && location === "/");
              const Icon = item.icon;
              return (
                <Link key={item.path} href={item.path} className="min-w-0 flex-1">
                  <button className="relative flex h-auto min-h-14 w-full min-w-0 flex-col items-center justify-center gap-0.5 px-1 py-1 transition-colors">
                    <Icon className={`h-5 w-5 transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`} />
                    <span className={`max-w-full break-words text-center text-[10px] font-medium leading-tight transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                      {t(item.labelKey, item.fallback)}
                    </span>
                    {isActive && <div className="absolute bottom-1 w-6 h-0.5 rounded-full bg-primary" />}
                  </button>
                </Link>
              );
            })}
            <button
              onClick={() => setMoreOpen(true)}
              className="flex h-auto min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1 transition-colors"
            >
              <LayoutGrid className={`h-5 w-5 transition-colors ${moreOpen ? "text-primary" : "text-muted-foreground"}`} />
              <span className={`max-w-full break-words text-center text-[10px] font-medium leading-tight transition-colors ${moreOpen ? "text-primary" : "text-muted-foreground"}`}>
                {t("nav.more", "More")}
              </span>
            </button>
          </div>
        </div>
      </div>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8 max-h-[85vh] overflow-y-auto">
          <SheetHeader className="flex-row items-center justify-between space-y-0 pr-10">
            <SheetTitle className="text-left">{t("nav.explore_features", "Explore Features")}</SheetTitle>
            <SheetDescription className="sr-only">
              {t("nav.explore_features_description", "Browse more ZEASTRO features and account options.")}
            </SheetDescription>
            <SettingsToggle />
          </SheetHeader>
          {roleLabel && (
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm font-semibold text-primary">
              <ShieldCheck className="h-4 w-4" />
              {roleLabel}
            </div>
          )}
          <div className="mt-4 space-y-5">
            {adminControl && (
              <div className="rounded-xl border border-primary/20 bg-primary/5" data-testid="nav-mobile-admin-control-center">
                <button
                  type="button"
                  onClick={() => setAdminMenuOpen((open) => !open)}
                  className={`flex w-full items-center gap-3 p-3 rounded-xl text-left transition-colors hover:bg-primary/10 ${
                    adminRouteOpen ? "text-primary" : "text-foreground"
                  }`}
                  aria-expanded={adminMenuOpen}
                  aria-controls="mobile-admin-control-center-menu"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-primary" />
                  </div>
                  <span className="flex-1 text-sm font-semibold">{adminControl.label}</span>
                  {adminMenuOpen ? (
                    <ChevronUp className="h-4 w-4 text-primary" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-primary" aria-hidden="true" />
                  )}
                </button>
                {adminMenuOpen && (
                  <div id="mobile-admin-control-center-menu" className="grid grid-cols-1 gap-1 border-t border-primary/15 p-2 min-[420px]:grid-cols-2">
                    {ADMIN_SECTIONS.filter((section) => canAccessAdminSection(user, section)).map((section) => {
                      const Icon = ADMIN_SECTION_ICONS[section.slug];
                      const active = isActiveAdminSection(location, section);
                      return (
                        <Link key={section.path} href={section.path} onClick={() => setMoreOpen(false)}>
                          <span
                             className={`flex min-w-0 items-start gap-2 rounded-lg px-2 py-2 text-xs leading-tight transition-colors ${
                              active
                                ? "bg-primary/10 text-primary font-medium"
                                : "text-muted-foreground hover:bg-accent/10 hover:text-foreground"
                            }`}
                            aria-current={active ? "page" : undefined}
                          >
                            <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                             <span className="min-w-0 break-words">{section.label}</span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {MORE_SECTIONS.map((section) => (
              <section key={section.headingKey}>
                <h3 className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                  {t(section.headingKey, section.headingFallback)}
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location === item.path;
                    return (
                      <Link key={item.path} href={item.path} onClick={() => setMoreOpen(false)}>
                <div className={`flex min-w-0 flex-col items-center gap-2 p-3 rounded-xl transition-colors hover:bg-accent/10 ${
                          isActive ? "bg-primary/10" : ""
                        }`}>
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Icon className="h-5 w-5 text-primary" />
                          </div>
                           <span className="max-w-full break-words text-center text-xs font-medium leading-tight text-foreground">{t(item.labelKey, item.fallback)}</span>
                           <FeaturePlanMarker
                             planTiers={item.planTiers}
                             currentTier={effectiveTier}
                             testId={`nav-more-plan-marker-${item.id}`}
                           />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
          {user && (
            <div className="mt-4 border-t border-border pt-4">
              <Link href="/profile" onClick={() => setMoreOpen(false)}>
                <span
                  className={`flex min-h-11 w-full min-w-0 items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-accent/10 ${
                    location === "/profile" ? "bg-primary/10 text-primary" : "text-foreground"
                  }`}
                  aria-current={location === "/profile" ? "page" : undefined}
                  data-testid="link-mobile-profile"
                >
                  <User className="h-4 w-4 flex-shrink-0" />
                  <span className="min-w-0 break-words">{t("nav.profile", "Profile")}</span>
                </span>
              </Link>
            </div>
          )}
          <div className={`${user ? "mt-1" : "mt-4 border-t border-border pt-4"}`}>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-4 py-3 rounded-xl text-left text-sm font-medium text-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
              data-testid="button-mobile-logout"
            >
              <LogOut className="h-4 w-4" />
              {t("nav.logout", "Log out")}
            </button>
          </div>
        </SheetContent>
      </Sheet>

    </>
  );
}
